import type { ForqSupabaseClient } from './client';
import type { AdminMasterEntry, MasterEntry, MastersGateway, SaveMasterEntryInput } from '@forq/masters-data';
import { SupabaseOperationError } from './errors';

interface Row {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly active: boolean;
  readonly sort_order: number;
  readonly updated_at?: string;
  readonly updated_by_name?: string | null;
}

function toEntry(row: Row): MasterEntry {
  return { id: row.id, code: row.code, name: row.name, active: row.active, sortOrder: row.sort_order };
}
function toAdmin(row: Row): AdminMasterEntry {
  return { ...toEntry(row), updatedAt: row.updated_at ?? '', updatedByName: row.updated_by_name ?? '' };
}

/** Kostenstellen + Warengruppen über die forq-RPCs (gleiche Verdrahtung wie die Systemmeldungen). */
export class SupabaseMastersGateway implements MastersGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`masters.${functionName}`, error.message ?? 'Der Vorgang ist fehlgeschlagen.');
    return data;
  }
  private async rows(functionName: string, tenantId: string): Promise<readonly Row[]> {
    const data = await this.rpc(functionName, { p_tenant_id: tenantId });
    return Array.isArray(data) ? (data as Row[]) : [];
  }
  private saveArgs(input: SaveMasterEntryInput): Record<string, unknown> {
    return { p_tenant_id: input.tenantId, p_id: input.id, p_code: input.code, p_name: input.name, p_active: input.active, p_sort_order: input.sortOrder };
  }

  async listCostCenters(tenantId: string) { return (await this.rows('list_cost_centers', tenantId)).map(toEntry); }
  async listWarengruppen(tenantId: string) { return (await this.rows('list_warengruppen', tenantId)).map(toEntry); }
  async adminListCostCenters(tenantId: string) { return (await this.rows('admin_list_cost_centers', tenantId)).map(toAdmin); }
  async adminListWarengruppen(tenantId: string) { return (await this.rows('admin_list_warengruppen', tenantId)).map(toAdmin); }
  async saveCostCenter(input: SaveMasterEntryInput) { await this.rpc('admin_save_cost_center', this.saveArgs(input)); }
  async saveWarengruppe(input: SaveMasterEntryInput) { await this.rpc('admin_save_warengruppe', this.saveArgs(input)); }
  async deleteCostCenter(tenantId: string, id: string) { await this.rpc('admin_delete_cost_center', { p_tenant_id: tenantId, p_id: id }); }
  async deleteWarengruppe(tenantId: string, id: string) { await this.rpc('admin_delete_warengruppe', { p_tenant_id: tenantId, p_id: id }); }
}
